import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { makeLabelTexture, makeIconTexture } from './jarTextures';

export const JAR_MODEL_URL = '/models/jar.glb';
export const JAR_SCALE = 25; // model is in metres (Ø40 × 30 mm) → scene units (Ø1 × 0.75)
export const JAR_RADIUS = 0.02 * JAR_SCALE;
export const JAR_HEIGHT = 0.02999 * JAR_SCALE;
const LABEL_Y = 0.195;

const BAND_GEO = new THREE.CylinderGeometry(JAR_RADIUS + 0.004, JAR_RADIUS + 0.004, 0.3, 64, 1, true);
const TOP_GEO = new THREE.CircleGeometry(0.44, 64);

const PART_PREFIXES = { Corpo: 'body', Tappo: 'lid', Giunzione: 'seam' };
const PART_OF = (name) => {
  const prefix = Object.keys(PART_PREFIXES).find((p) => name.startsWith(p));
  return prefix ? PART_PREFIXES[prefix] : null;
};

let modelPromise = null;
export const loadJarModel = () => modelPromise || (modelPromise = new Promise((resolve, reject) => {
  new GLTFLoader().load(JAR_MODEL_URL, (gltf) => {
    const parts = {};
    gltf.scene.traverse((o) => {
      const key = o.isMesh ? PART_OF(o.name) : null;
      if (key) parts[key] = o.geometry;
    });
    if (!parts.body || !parts.lid) { reject(new Error('Jar model incomplete')); return; }
    resolve(parts);
  }, undefined, reject);
}));

export const buildJar = (parts, { color, icon, label, hoverIntensity = 0.3, idleIntensity = 0.06 }) => {
  const c = new THREE.Color(color);
  const base = { roughness: 0.34, metalness: 0.03, clearcoat: 0.45, clearcoatRoughness: 0.25, emissive: c, emissiveIntensity: idleIntensity };
  const bodyMat = new THREE.MeshPhysicalMaterial({ color: c, ...base });
  const lidMat = new THREE.MeshPhysicalMaterial({ color: c.clone().offsetHSL(0, 0, 0.03), ...base, roughness: 0.3 });
  const seamMat = new THREE.MeshPhysicalMaterial({ color: c.clone().multiplyScalar(0.3), roughness: 0.55, metalness: 0, clearcoat: 0.1 });

  const model = new THREE.Group();
  model.scale.setScalar(JAR_SCALE);
  model.add(new THREE.Mesh(parts.body, bodyMat), new THREE.Mesh(parts.lid, lidMat));
  if (parts.seam) model.add(new THREE.Mesh(parts.seam, seamMat));

  const labelTex = makeLabelTexture(label);
  const band = new THREE.Mesh(BAND_GEO, new THREE.MeshBasicMaterial({ map: labelTex, transparent: true, depthWrite: false, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -1 }));
  band.position.y = LABEL_Y;
  band.rotation.y = Math.PI;

  const iconTex = makeIconTexture(icon);
  const top = new THREE.Mesh(TOP_GEO, new THREE.MeshBasicMaterial({ map: iconTex, transparent: true, opacity: 0.95, toneMapped: false, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }));
  top.position.y = JAR_HEIGHT + 0.002;
  top.rotation.x = -Math.PI / 2;

  const group = new THREE.Group();
  group.add(model, band, top);

  return {
    group,
    setHover: (on) => { bodyMat.emissiveIntensity = lidMat.emissiveIntensity = on ? hoverIntensity : idleIntensity; },
    dispose: () => { [bodyMat, lidMat, seamMat, band.material, top.material].forEach((m) => m.dispose()); labelTex.dispose(); iconTex.dispose(); },
  };
};
